import { useState, useEffect } from 'react';
import { useNavigate } from '@usas/ui-web/wrappers/ReactRouterDom';

import { isPersonObjectDataCurrent } from '../utils/MemberUtils';

import useNavRoutes from '@usas/ui-web/state/security/UseNavRoutes';
import useBasePersonData from '@usas/ui-web/state/personBase/UseBasePersonData';
import usePersonGeneralData from '@usas/ui-web/state/personGeneral/UsePersonGeneralData';

import Constants from '@usas/ui-web/utils/Constants';
import { formatDate } from '@usas/ui-web/utils/DateFunctions';
import { evaluateBirthDate, formatPhoneNumber } from '@usas/ui-web/utils/validation';

const SELF_CONTACT_NAME = 'Self';

const useMemberContacts = () => {
  const navigate = useNavigate();
  const { navRoutes } = useNavRoutes();
  const { basePersonState, getBasePerson } = useBasePersonData();
  const { personGeneralState, getPersonGeneral, deletePersonContact, deletePersonContactPhone, deletePersonContactAddress,
    confirmSave } = usePersonGeneralData();
  const [viewState, setViewState] = useState({ contactDisplayList: [] });
  const [deleteModalState, setDeleteModalState] = useState(getInitialDeleteModalState());

  const onAddPerson = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    navigate(navRoutes.MEMBER_CONTACTS_DETAIL.route, { state: { personContactId: Constants.DEFAULT_ID } });
  };

  const onEditPerson = (e, personContactId) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    navigate(navRoutes.MEMBER_CONTACTS_DETAIL.route, { state: { personContactId } });
  };

  const onAddEditAddress = (e, personContactAddressId, personContactId) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    navigate(navRoutes.MEMBER_CONTACTS_DETAIL_ADDRESS?.route, { state: { personContactAddressId, personContactId } });
  };

  const onAddEditPhone = (e, personContactPhoneId, personContactId) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    navigate(navRoutes.MEMBER_CONTACTS_DETAIL_PHONE?.route, { state: { personContactPhoneId, personContactId } });
  };

  const onDeletePerson = (e, personContactId, targetObjectName) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    setDeleteModalState({
      ...deleteModalState,
      modalTitle: 'Delete this Contact?',
      personContactId,
      targetObjectName,
      displayPopUp: true
    });
  };

  const onDeleteAddress = (e, personContactAddressId, targetObjectName) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    setDeleteModalState({
      ...deleteModalState,
      modalTitle: 'Delete this Address?',
      personContactAddressId,
      targetObjectName,
      displayPopUp: true
    });
  };

  const onDeletePhone = (e, personContactPhoneId, targetObjectName) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    setDeleteModalState({
      ...deleteModalState,
      modalTitle: 'Delete this Phone Number?',
      personContactPhoneId,
      targetObjectName,
      displayPopUp: true
    });
  };

  const onDeleteModalConfirmClicked = () => {
    if (deleteModalState.personContactId > 0) {
      deletePersonContact(deleteModalState.personContactId);
    } else if (deleteModalState.personContactAddressId > 0) {
      deletePersonContactAddress(deleteModalState.personContactAddressId);
    } else if (deleteModalState.personContactPhoneId > 0) {
      deletePersonContactPhone(deleteModalState.personContactPhoneId);
    }

    setDeleteModalState(getInitialDeleteModalState());
  };

  const onDeleteModalCanceledClicked = () => {
    setDeleteModalState(getInitialDeleteModalState());
  };

  useEffect(() => {
    if (personGeneralState.isObjLoaded === false
      || !isPersonObjectDataCurrent(basePersonState.objData.personId, personGeneralState.objData)) {
      getPersonGeneral(basePersonState.objData.personId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basePersonState]);

  useEffect(() => {
    if (basePersonState.isObjLoaded === true && personGeneralState.isObjLoaded === true
      && isPersonObjectDataCurrent(basePersonState.objData.personId, personGeneralState.objData)) {
      setViewState({
        ...viewState,
        contactDisplayList: createContactDisplayList(basePersonState.objData, personGeneralState.objData.personContact || [])
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personGeneralState]);

  useEffect(() => { // base & general share self contact values
    if (personGeneralState.isSaved === true) {
      getBasePerson(personGeneralState.objData.personId);
      confirmSave();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personGeneralState.isSaved]);

  function getPrimaryPhoneNumber(phoneArray) {
    if (Array.isArray(phoneArray) && phoneArray.length > 0) {
      const primaryPhone = phoneArray.find(e => e.isPrimary === true);

      if (primaryPhone) {
        return primaryPhone.phoneNumber ? formatPhoneNumber(primaryPhone.phoneNumber) : '';
      } else {
        return phoneArray[0]?.phoneNumber ? formatPhoneNumber(phoneArray[0]?.phoneNumber) : '';
      }
    } else {
      return '';
    }
  };

  function createContactDisplayList(personObj, personContacts) {
    const memberBirthDate = formatDate(personObj.birthDate);
    const birthDateObj = evaluateBirthDate(memberBirthDate);
    const contactList = [];

    if (Array.isArray(personContacts) && personContacts.length > 0) {
      for (const contact of personContacts) {
        if (contact.contactRelationshipType?.typeName === SELF_CONTACT_NAME) {
          contactList.unshift({
            id: contact.personContactId || Constants.DEFAULT_ID,
            name: `${contact.firstName || ''} ${contact.lastName || ''}`,
            relationship: contact.contactRelationshipType?.typeName || '',
            email: contact.emailAddress || '',
            phoneNumber: getPrimaryPhoneNumber(contact.personContactPhone),
            isPrimaryDisplay: '',
            canEdit: birthDateObj.isJunior === true || birthDateObj.isAdult === true,
            canDelete: false,
            contactPhones: contact.personContactPhone,
            contactAddresses: contact.personContactAddress
          });
        } else {
          contactList.push({
            id: contact.personContactId || Constants.DEFAULT_ID,
            name: `${contact.firstName || ''} ${contact.lastName || ''}`,
            relationship: contact.contactRelationshipType?.typeName || '',
            email: contact.emailAddress || '',
            phoneNumber: getPrimaryPhoneNumber(contact.personContactPhone),
            isPrimaryDisplay: contact.isPrimary === true ? 'Yes' : 'No',
            canEdit: true,
            canDelete: contact.isPrimary === true ? false : true,
            contactPhones: contact.personContactPhone,
            contactAddresses: contact.personContactAddress
          });
        }
      }
    }

    return contactList;
  };

  function getInitialDeleteModalState() {
    return {
      personContactAddressId: Constants.DEFAULT_ID,
      personContactPhoneId: Constants.DEFAULT_ID,
      personContactId: Constants.DEFAULT_ID,
      targetObjectName: '',
      modalTitle: 'Place Holder',
      displayPopUp: false
    };
  };

  return {
    deleteModalState,
    viewState,
    basePersonState,
    personGeneralState,
    onAddPerson,
    onEditPerson,
    onAddEditAddress,
    onAddEditPhone,
    onDeletePerson,
    onDeleteAddress,
    onDeletePhone,
    onDeleteModalConfirmClicked,
    onDeleteModalCanceledClicked
  };
};

export default useMemberContacts;